import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckIcon, CloseIcon, EditIcon } from '@chakra-ui/icons';
import { Button, Center, Flex, HStack, Text, Tooltip } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import { FormGroup, NumberInput } from '../../../../../common/components';
import { useTranslations } from '../../../../../contexts/LocalizationContext';
import { useUpdateRestaurantInventoryCount } from '../../../../../services/inventory-counts';
import { InventoryCountStatus } from '../../models/enums/inventory-count-status.enum';
import { InventoryCountFormValues } from '../../models/inventory-count-form-values';
import { InventoryCountResponse } from '../../models/inventory-count-response';
import { getInventoryCountInventory } from '../../utils/inventory-counts.helper';
import { useInventoryCountInventoryValidationSchema } from './hooks/use-inventory-count-inventory-validation-schema.hook';

type InventoryCountInventoryProps = {
  model: InventoryCountResponse;
};

export const InventoryCountInventory = ({ model }: InventoryCountInventoryProps) => {
  const translations = useTranslations();
  const validationSchema = useInventoryCountInventoryValidationSchema();
  const [isEditMode, setIsEditMode] = useState(false);
  const { mutate: updateInventoryCountInventory } = useUpdateRestaurantInventoryCount();

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm<InventoryCountFormValues>({ resolver: yupResolver(validationSchema) });

  useEffect(() => {
    reset(model);
  }, [reset, model]);

  const handleFormSubmit = handleSubmit(({ ...form }: InventoryCountFormValues) => {
    const { actualStock, shouldStock } = form;
    updateInventoryCountInventory({
      inventoryCountId: model.id,
      values: {
        ...(actualStock !== null && { scannedStockQuantity: actualStock }),
        ...(shouldStock !== null && { shouldStockQuantity: shouldStock }),
      },
    });
  });
  const handleEditMode = useCallback(() => setIsEditMode(!isEditMode), [isEditMode]);
  const handleCancel = useCallback(() => {
    reset(model);
    handleEditMode();
  }, [handleEditMode, model, reset]);

  return isEditMode ? (
    <form onSubmit={handleFormSubmit}>
      <Flex gap={2}>
        <FormGroup label={''} inputId="shouldStock" validationError={errors.shouldStock?.message}>
          <NumberInput name="shouldStock" color={'grey.900'} register={register('shouldStock')} onChange={setValue} />
        </FormGroup>
        <Center>{'/'}</Center>
        <FormGroup label={''} inputId="actualStock" validationError={errors.actualStock?.message}>
          <NumberInput name="actualStock" color={'grey.900'} register={register('actualStock')} onChange={setValue} />
        </FormGroup>
        <Center>
          <Tooltip label={translations('save')}>
            <Button type={'submit'} p={0} data-testid="inventory-save-icon">
              <CheckIcon cursor="pointer" color={'green.500'} w={3} />
            </Button>
          </Tooltip>
          <Tooltip label={translations('cancel')}>
            <Button onClick={handleCancel} ml={2}>
              <CloseIcon cursor="pointer" color={'red.500'} w={3} />
            </Button>
          </Tooltip>
        </Center>
      </Flex>
    </form>
  ) : (
    <>
      <HStack justifyContent="center">
        <Text>{getInventoryCountInventory(model)}</Text>
        {model.status !== InventoryCountStatus.INVENTORY_IN_PROGRESS ? (
          <Tooltip label={translations('edit')}>
            <EditIcon data-testid="inventory-edit-icon" cursor="pointer" onClick={handleEditMode} ml={2} />
          </Tooltip>
        ) : null}
      </HStack>
    </>
  );
};
